import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { OpportunitiesService } from '@inference.engine/api-client/web';
import { inferenceHttp } from '@/lib/api/http-client';
import { notificationService } from '@/lib/services/notification.service';
import { track } from '@/shared/analytics';

interface ClearWorkspaceOpportunitiesParams {
  workspaceId: string;
  workspaceName: string;
  dateStart?: Date;
  dateEnd?: Date;
}

export const useClearWorkspaceOpportunities = () => {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ClearWorkspaceOpportunitiesParams) => {
      const response = await OpportunitiesService.clearWorkspaceOpportunities({
        client: inferenceHttp,
        query: {
          workspaceId: params.workspaceId,
          dateStart: params.dateStart?.toISOString(),
          dateEnd: params.dateEnd?.toISOString(),
        },
      });
      return response.data as { deletedCount: number };
    },
    onSuccess: async (data, variables) => {
      // Invalidate all opportunities queries for the affected workspace
      await queryClient.invalidateQueries({
        queryKey: ['opportunities'],
      });

      // Show success notification with workspace context
      const message =
        variables.dateStart || variables.dateEnd
          ? t('admin.useClearWorkspaceOpportunities.successfullyClearedOpportunitiesInDateRange', {
              deletedCount: data.deletedCount,
              workspaceName: variables.workspaceName,
            })
          : t('admin.useClearWorkspaceOpportunities.successfullyClearedAllOpportunities', {
              deletedCount: data.deletedCount,
              workspaceName: variables.workspaceName,
            });

      notificationService.showSuccess(message);

      // Track analytics
      // eslint-disable-next-line i18next/no-literal-string -- analytics property value
      const deletionMode = variables.dateStart || variables.dateEnd ? 'date_range' : 'all';
      const props: {
        workspace_id: string;
        deleted_count: number;
        deletion_mode: 'date_range' | 'all';
        date_range_days?: number;
      } = {
        workspace_id: variables.workspaceId,
        deleted_count: data.deletedCount,
        deletion_mode: deletionMode,
      };

      // Calculate date range in days if date range mode
      if (deletionMode === 'date_range' && variables.dateStart && variables.dateEnd) {
        const diffMs = variables.dateEnd.getTime() - variables.dateStart.getTime();
        props.date_range_days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      }

      track('workspace_opportunities_cleared', props);
    },
    onError: (error) => {
      notificationService.showError(
        t('admin.useClearWorkspaceOpportunities.failedToClearOpportunities'),
        {
          description: error.message,
        },
      );
    },
  });
};
